// AriaOne Learning Center — curriculum (client-facing).
// A self-paced "self-pass" framework for the people who USE AriaOne day to day.
// Learners read, then tick self-attestation items. A level clears when every
// checklist item is ticked; clearing a level unlocks the next.
//
// Each level lists `modules` — soft links that open the real app module in a new
// tab (session carried over) so learners can practise what they just read.
//
// Item ids are derived as `${group.id}-${index}` and must stay stable (progress
// is keyed on them). Reading sections do NOT count toward completion; only the
// `items` and `qa` entries inside `groups` do.

export const ABOUT = {
  title: "About AriaOne",
  blurb:
    "AriaOne brings scheduling, clinical notes and billing together in one place. You can book and run telehealth visits, let the system draft your visit notes and suggest the right codes, upload and read documents, build and send claims, and post the payments that come back — all from a single login. This course walks you through the whole product, one level at a time, so you can learn at your own pace.",
};

export const LEVELS = [
  // ───────────────────────────────── LEVEL 1 ─────────────────────────────────
  {
    id: 1,
    key: "junior",
    tag: "Junior",
    title: "Getting started",
    target: "Your first days with AriaOne.",
    goal: "Get around confidently, find a patient, book a visit, upload a document, and read your dashboard.",
    modules: [
      { label: "Home", path: "/home" },
      { label: "Patients", path: "/patients" },
      { label: "Telehealth", path: "/telehealth-v2" },
      { label: "Encounter review", path: "/encounter-review" },
      { label: "Chart documents", path: "/patient-documents" },
    ],
    reading: [
      {
        h: "What AriaOne does for you",
        p: "AriaOne is where your practice schedules visits, writes and signs clinical notes, and handles billing. Book a telehealth visit, run it, and the system drafts the note and suggests the diagnosis and procedure codes. Upload documents and they're read for you. Build claims, send them, and post the payments that come back — all in one app.",
      },
      {
        h: "Three areas, one app",
        p: "The left menu is grouped into three areas: Scheduling (booking and telehealth visits), Clinical (visit notes, dictation and documents) and Billing (claims and payments). You move between them from the same menu, and your work flows naturally from one to the next.",
      },
      {
        h: "Your practice",
        p: "Your account can hold more than one practice (each is called a client). You're always working in one practice at a time, and every screen shows that practice's patients, visits and claims. If something you expect isn't showing, make sure you're in the right practice first.",
      },
      {
        h: "Reading the menu at a glance",
        p: "Small numbers next to menu items tell you what needs attention: Encounter review shows notes waiting to be signed, Claims queue shows claims that need a refresh, and the document pages show files still being read. The activity indicator shows when the system is busy with your uploads and notes, or idle.",
      },
    ],
    groups: [
      {
        id: "l1-a",
        title: "Finding your way around",
        items: [
          "I can confirm which practice I'm currently working in.",
          "I can collapse and expand the left menu and find Settings, the text-size control and my account menu.",
          "I can name the three areas of the menu: Scheduling, Clinical and Billing.",
          "I can explain what each menu number means (notes to sign, claims to refresh, documents still reading).",
          "I can open each main page from the menu: Home, Telehealth, Encounter review, Dictation, Chart documents, Clinic documents, Claims queue, Payment posting and Patients.",
        ],
      },
      {
        id: "l1-b",
        title: "Your dashboard (Home)",
        items: [
          "I can describe the three Home areas: what needs me, my uploads in progress, and recent activity.",
          "I can read the 'needs you' items and open one to act on it.",
          "I can see today's uploads and where they go once they finish.",
        ],
      },
      {
        id: "l1-c",
        title: "Patients",
        items: [
          "I can search the Patients directory by name or MRN.",
          "I can read a patient row (age, DOB, MRN, provider, insurance, last visit, next appointment).",
          "I can open a patient's chart from the directory.",
          "I can use 'Load more' to see more patients.",
        ],
      },
      {
        id: "l1-d",
        title: "Booking a visit",
        items: [
          "I can switch between the Schedule and Activity tabs in Telehealth.",
          "I can filter visits by status, date range, provider and search text.",
          "I can switch between List and Week views.",
          "I can book a scheduled visit: choose the patient, visit type/reason, contact, provider, pick a time, and Book.",
          "I can copy the patient's link so they can join the visit.",
          "I can tell the difference between a scheduled visit and an on-demand ('Book for now') visit.",
          "I can reschedule, complete or start a visit from its row.",
        ],
      },
      {
        id: "l1-e",
        title: "Notes & documents (first look)",
        items: [
          "I can find a visit's note in Encounter review and see its suggested codes next to it.",
          "I can see whether a note is signed or still waiting to be signed.",
          "I can upload a document to Chart documents and watch it being read.",
          "I know the file types and size limit: PDF, JPG, PNG or HEIC, up to 50 MB each.",
        ],
      },
      {
        id: "l1-f",
        title: "Good habits",
        items: [
          "When something looks missing, I check the active practice and the date range first.",
          "I know where to find help and what details to note (practice, page, patient/visit, what I clicked).",
        ],
      },
      {
        id: "l1-kc",
        title: "Knowledge check — read the answer, then self-confirm",
        qa: [
          { q: "A patient you expect to see now seems to be missing. What's the first thing to check?", a: "Which practice you're working in — make sure you're in the right one. Every screen only shows the active practice." },
          { q: "Where do you see visit notes that still need signing?", a: "In Encounter review — the menu shows a count of notes waiting to be signed." },
          { q: "Encounter review looks empty. Besides the practice, what else should you check?", a: "The date range — it starts at the last 7 days, so widen it (e.g. to 30 days)." },
          { q: "What file types and size can you upload to Chart documents?", a: "PDF, JPG, PNG or HEIC, up to 50 MB each." },
          { q: "What's the difference between a scheduled visit and 'Book for now'?", a: "Scheduled picks a set time from open slots; 'Book for now' starts an on-demand visit right away and the provider presses Start when ready." },
          { q: "How do you give a patient their link to join a telehealth visit?", a: "Use Copy patient link on the visit and send it to them." },
          { q: "What does the activity indicator ('Working' / 'Idle') tell you?", a: "Whether the system is currently processing your uploads and notes, or has nothing running." },
          { q: "Where does a completed telehealth visit show up afterwards?", a: "On the Activity tab, where its note is prepared." },
        ],
      },
      {
        id: "l1-pt",
        title: "Practical tasks — do them in the app, then tick",
        items: [
          "Confirm which practice you're in, find a patient by MRN, and open the chart.",
          "Book a scheduled telehealth visit and copy the patient link.",
          "Widen Encounter review to the last 30 days and open a note.",
          "Upload a PDF to Chart documents and watch it being read.",
          "Read your Home 'needs you' list and open one item.",
          "Find the account menu and the text-size control.",
        ],
      },
    ],
  },

  // ───────────────────────────────── LEVEL 2 ─────────────────────────────────
  {
    id: 2,
    key: "mid",
    tag: "Mid",
    title: "Your daily workflow",
    target: "Handling your everyday work on your own.",
    goal: "Run a visit from booking to a finished note, review and sign, fix insurance, build a claim, and post a payment.",
    modules: [
      { label: "Telehealth", path: "/telehealth-v2" },
      { label: "Encounter review", path: "/encounter-review" },
      { label: "Dictation", path: "/dictation" },
      { label: "Chart documents", path: "/patient-documents" },
      { label: "Claims queue", path: "/create-claims" },
      { label: "Payment posting", path: "/payments-v2" },
    ],
    reading: [
      {
        h: "From visit to note",
        p: "After a telehealth visit (or a dictation), AriaOne drafts the note and suggests the diagnosis and procedure codes. You review it in the note editor, make any changes, and sign it. Signing is what makes the note ready to bill.",
      },
      {
        h: "The note editor & 'Pull from chart'",
        p: "The editor opens from several places — Encounter review, Dictation, a claim, or the visit itself. 'Pull from chart' lets you add the patient's allergies, medications, problems, vitals, labs and documents into the note. After editing, 'Save & recode' rebuilds the note and refreshes the suggested codes; a plain Save keeps your text but marks the codes as needing a refresh.",
      },
      {
        h: "Insurance made simple",
        p: "The Manage insurance panel (opened from the insurance chips, the coverage bar, or the claim) lets you set the primary and secondary payer, search for the correct payer, read a scanned insurance card, and copy coverage from history. When you save the correct payer once, AriaOne remembers it and updates other waiting visits with the same payer.",
      },
      {
        h: "Claims & payments",
        p: "The Claims queue is where claims are built — open one to see the diagnoses, service lines and anything that still needs fixing before it can be saved. You can upload a stack of claim documents and AriaOne splits them by patient and date. Payment posting is where remittances (EOBs/ERAs) come in and get reconciled.",
      },
    ],
    groups: [
      {
        id: "l2-a",
        title: "The note editor",
        items: [
          "I can open the note editor from Encounter review, Dictation, a claim and a visit.",
          "I can switch between the custom note and the standard SOAP view and know which one the codes read from.",
          "I can use 'Pull from chart' to add allergies, medications, problems, vitals, labs and documents.",
          "I can see what's staged, add it to the note, and recognise the 'not saved yet' banner.",
          "I can explain the difference between Save and Save & recode.",
          "I know Cancel asks again before discarding, and leaving warns me about unsaved changes.",
        ],
      },
      {
        id: "l2-b",
        title: "Insurance",
        items: [
          "I can open Manage insurance from the visit chips, the coverage bar and the claim.",
          "I can search for and set the correct payer when one is missing.",
          "I can add or edit the primary and secondary payer (member ID, group).",
          "I can use 'on file → use this' when the matched payer differs from billing.",
          "I can scan an insurance card to fill the payer in.",
          "I can copy a previous coverage to primary or secondary and confirm the change.",
          "I can use the 'needs insurance' group fix to set the payer once for many visits at a time.",
        ],
      },
      {
        id: "l2-c",
        title: "Telehealth, end to end",
        items: [
          "I can book from Week view for one or several providers and use quick-book.",
          "I can handle a 'that time was just taken' message when booking or rescheduling.",
          "I can use the time picker (morning/afternoon/evening, slot length, next opening).",
          "I can use the row actions: Start, Copy link, Open chart, Message, Rebook, Reschedule, Complete, End session, Delete.",
          "I can guide a provider through the in-visit panel (summary, tabs, patient documents).",
          "I can tell when a visit needs a retry and press it, and recognise 'the patient never joined'.",
        ],
      },
      {
        id: "l2-d",
        title: "Review & sign",
        items: [
          "I can fix a note and sign it: edit → refresh codes → sign.",
          "I can unsign to make a change and understand the warning if a claim was already created.",
          "I can read the codes panel: reorder or remove diagnoses, set procedure units and pointers, review prescriptions.",
          "I can see the evidence behind a suggested diagnosis.",
          "I can open the change history of a note, read who changed what, and download it.",
        ],
      },
      {
        id: "l2-e",
        title: "Dictation",
        items: [
          "I can dictate into a patient's note and close the tab.",
          "I can read the sessions list (insurance, code counts, charge, claim status).",
          "I can refresh codes after editing and open the related claim.",
        ],
      },
      {
        id: "l2-f",
        title: "Documents",
        items: [
          "I can upload one document, several, or a batch that AriaOne splits automatically.",
          "I know patient documents go to Chart documents and letters/notices go to Clinic documents.",
          "I can watch the split / reading tracker.",
          "I can search, sort and filter the document list, and retry a file that didn't read.",
        ],
      },
      {
        id: "l2-g",
        title: "Claims",
        items: [
          "I can upload claim documents (choose the provider, up to 10 files) and let AriaOne split them by patient and date.",
          "I can resolve a 'to review' item by matching the patient and a date — no re-upload.",
          "I know remittances don't go through claim upload.",
          "I can finish a claim: diagnoses, service lines (code, units, charge, pointers) and totals, and fix warnings.",
          "I can read why a claim can't be saved yet and clear it.",
          "I can refresh out-of-date claims.",
        ],
      },
      {
        id: "l2-h",
        title: "Payments",
        items: [
          "I can upload a remittance (single or a bundle) and know duplicates are caught.",
          "I can search and filter the ledger (check number, amount, payer).",
          "I can open the ERA workspace and find the reconciliation table and the readiness checks.",
          "I can set the payer and billing NPI, generate the 835 and download it.",
        ],
      },
      {
        id: "l2-kc",
        title: "Knowledge check — read the answer, then self-confirm",
        qa: [
          { q: "You added chart items to a note but the codes look stale. What do you do?", a: "Refresh the codes — use Recode (or Save & recode in the editor)." },
          { q: "You fixed one visit's payer. Will other visits with the same payer update?", a: "Yes — saving the correct payer teaches AriaOne and updates other waiting visits with that payer (a message tells you how many)." },
          { q: "Many visits share the same missing payer. What's the fastest fix?", a: "Use the 'needs insurance' group fix — set the payer once and apply it to all of them." },
          { q: "A claim batch left two items 'to review'. What do they need?", a: "Match each to the right patient and a date of service; there's no need to re-upload." },
          { q: "'Save claim' is greyed out. Why might that be?", a: "The visit isn't signed, or there are still items to fix before it can save (for example a missing diagnosis pointer)." },
          { q: "A signed note needs an edit. What happens?", a: "You'll be asked to unsign first, then edit, refresh the codes and sign again." },
          { q: "What two things should be set before generating the 835?", a: "The primary payer and the billing NPI." },
          { q: "Where can you see exactly which code a refresh added, and when?", a: "In the note's change history — and you can download it." },
          { q: "What does an amber dot on the Activity tab mean?", a: "A visit needs a retry — its note didn't finish; press Retry." },
          { q: "Why might a patient show 'no chart' when booking?", a: "They aren't linked to a chart yet." },
        ],
      },
      {
        id: "l2-pt",
        title: "Practical tasks — do them in the app, then tick",
        items: [
          "Book a visit from Week-view quick-book, then reschedule it.",
          "Pull two chart items into a note and Save & recode.",
          "Fix a missing payer, then add a secondary payer.",
          "Use the 'needs insurance' group fix.",
          "Upload a claim bundle and resolve a 'to review' item.",
          "Clear a blocker and save a claim.",
          "Open a note's change history and download it.",
          "Upload a remittance, set the payer and NPI, generate and download the 835.",
        ],
      },
    ],
  },

  // ───────────────────────────────── LEVEL 3 ─────────────────────────────────
  {
    id: 3,
    key: "expert",
    tag: "Expert",
    title: "Setup & tricky cases",
    target: "Setting up your practice and handling the harder cases.",
    goal: "Set up a practice from scratch, reconcile difficult remittances, use the change history for audits, and read the activity dashboard to know where things stand.",
    modules: [
      { label: "Settings", path: "/settings" },
      { label: "Claims queue", path: "/create-claims" },
      { label: "Payment posting", path: "/payments-v2" },
      { label: "Lumina at work", path: "/luminaATwork" },
      { label: "Encounter review", path: "/encounter-review" },
    ],
    reading: [
      {
        h: "Setting up a practice",
        p: "Practice Setup walks through everything a practice needs, in order: the practice and its providers (with NPIs), connections, your payer list, rendering and billing relationships, availability, intake, locations, codes & modifiers, your note template, visit types and coding rules. Earlier steps feed later ones — for example, linking a provider's NPI makes that provider available to pick on claims.",
      },
      {
        h: "Availability, templates & visit types",
        p: "Availability sets the hours patients can book, which is exactly what the Telehealth time picker then offers. Your note template is learned from a sample you upload — it learns the layout, never patient details — and you confirm the exam defaults before they pre-fill. Visit types can be auto-suggested from your providers, then fine-tuned across their settings tabs.",
      },
      {
        h: "Coding rules & the change history",
        p: "You can set 'always / never' coding rules at the practice level and per provider; a provider inherits the practice rules and adds its own on top. Every note change is kept in a permanent, read-only history you can download — useful for audits and for answering 'who changed this, and when'.",
      },
      {
        h: "Knowing where things stand",
        p: "The activity dashboard ('Lumina at work') shows what the system is doing right now and how long things typically take — document reading, note coding, remittance reading, telehealth notes and dictations — so you can see whether something is still processing or already finished.",
      },
    ],
    groups: [
      {
        id: "l3-a",
        title: "Setting up a practice",
        items: [
          "I can set up a new practice through the full Practice Setup, in order.",
          "I can link a provider's NPI and see them become available to pick on a claim.",
          "I can build availability with more than one window per day, copy a day across the week, and see the result in the Telehealth time picker.",
          "I can build an intake form with questions and guidance and preview it.",
          "I can teach a note template from a sample, turn on and confirm the exam defaults, and preview it.",
          "I can auto-suggest visit types from my providers and then edit one across its tabs (basics, booking, note, billing, programs, advanced).",
          "I can set a default visit type for imports that don't match.",
          "I can write coding rules at the practice and provider level and see the rule count on a claim.",
        ],
      },
      {
        id: "l3-b",
        title: "Change history & audits",
        items: [
          "I can read each kind of history entry (note edited, codes refreshed, signed, unsigned) and its colour.",
          "I can rebuild a timeline from a downloaded history to answer 'who changed what, when'.",
          "I can read the details of a change (codes added or removed, reasons) and the before/after.",
          "I understand the history is permanent and can't be edited, and can explain that to a colleague.",
        ],
      },
      {
        id: "l3-c",
        title: "Harder remittances",
        items: [
          "I can reconcile a remittance with several claims and lines and use the auto-balance helper.",
          "I can add and remove adjustments (a reason code plus an amount).",
          "I can read the readiness checks and clear them before generating the 835.",
          "I can tell why a generate failed from the list of issues.",
          "I can work the ledger at scale: generate all, download all, save, delete, and find failed or held items.",
        ],
      },
      {
        id: "l3-d",
        title: "Reading activity",
        items: [
          "I can read each activity card (documents, splits, coding, remittances, telehealth, dictations): running now, idle, done, and typical time.",
          "I understand which figures are live and which are approximate.",
          "I can match a stuck visit, a long-running upload or a slow refresh to the matching card.",
          "I can use my usage stats (today / 7 / 30 days) when something feels slow.",
        ],
      },
      {
        id: "l3-e",
        title: "Tricky cases I can handle",
        items: [
          "Two insurances: set primary and secondary and confirm the '2nd' badge shows.",
          "A payer read wrong from a document: use 'on file vs billing', copy from history, or scan the card, and confirm other visits updated.",
          "A visit unsigned after a claim was made: re-sign, refresh the codes, and save again.",
          "A batch put pages under the wrong patient or date: reopen the batch and resolve it.",
          "A custom note format gives surprising codes: switch to the standard view to see what was coded, then refresh.",
          "A claim shows 'estimated' totals: find the missing rates in setup.",
          "A telehealth visit nobody joined: pick the right next step (rebook, end session, or delete).",
        ],
      },
      {
        id: "l3-kc",
        title: "Knowledge check — read the answer, then self-confirm",
        qa: [
          { q: "You linked a provider's NPI. Where does that show up next?", a: "In the list of rendering providers you can pick on the claim, and in the billing relationships." },
          { q: "Why does auto-suggesting visit types need a provider linked?", a: "The suggestions come from your linked providers' specialties." },
          { q: "What does the note template learn from your sample, and what must happen before exam defaults pre-fill?", a: "It learns the layout only (never patient details); you must confirm the exam defaults first." },
          { q: "A practice rule and a provider rule both apply to one provider — which wins?", a: "Both apply — the provider inherits the practice rules and adds its own on top." },
          { q: "Generating the 835 failed. Where do you look first?", a: "The readiness checks, the list of issues from the generate, and the payer / billing NPI settings." },
          { q: "Encounter review says 14 need insurance, but today's filter shows only 3 visits. Why?", a: "The 'needs insurance' count is for the whole practice, not just the current filter." },
          { q: "Which activity figures are exact and which are approximate?", a: "Today / live is exact; other time windows are approximate." },
          { q: "A colleague wants a history entry deleted because it was a mistake. What do you say?", a: "It can't be — the history is permanent and read-only; make a correcting change instead." },
          { q: "Why do the same insurance cards appear across the chart, documents and the insurance panel?", a: "They all read from one shared document store for that patient." },
          { q: "A page 'looks a bit different' from another. Is that a problem?", a: "No — it's just styling; the actions and data are the same." },
        ],
      },
      {
        id: "l3-pt",
        title: "Practical tasks — do them in the app, then tick",
        items: [
          "Set up a new practice through all the steps (demo).",
          "Build availability with two windows a day and see the slots in Telehealth.",
          "Teach a note template from a sample, confirm exam defaults, and preview.",
          "Auto-suggest visit types, then edit one across its tabs.",
          "Write a practice and a provider coding rule and see them on a claim.",
          "Reconcile a remittance with an adjustment and a first failed generate.",
          "From a downloaded change history, answer who added a diagnosis and when.",
          "Use the activity dashboard to see whether a batch is still running.",
        ],
      },
    ],
  },

  // ───────────────────────────────── LEVEL 4 ─────────────────────────────────
  {
    id: 4,
    key: "champion",
    tag: "Champion",
    title: "Power user & team lead",
    target: "Becoming your practice's AriaOne expert.",
    goal: "Know the whole product well enough to teach it, run a smooth setup for a new practice, and lead your team day to day.",
    modules: [
      { label: "Home", path: "/home" },
      { label: "Settings", path: "/settings" },
      { label: "Telehealth", path: "/telehealth-v2" },
      { label: "Claims queue", path: "/create-claims" },
      { label: "Payment posting", path: "/payments-v2" },
    ],
    reading: [
      {
        h: "The whole picture",
        p: "By now you can connect the dots: set up a practice, book and run visits, let notes and codes draft themselves, review and sign, build and send claims, and post the payments that come back — and you know the same patient's information shows consistently across the chart, documents and insurance.",
      },
      {
        h: "The full journey",
        p: "Settings → book a visit → run it with the in-visit panel → the note is prepared → review and sign → build the claim → the remittance comes back → generate the 835 → and it's all recorded in the change history. You can walk a new teammate through this without notes.",
      },
      {
        h: "Every signal explained",
        p: "You can explain what each number, badge and status means — notes to sign, claims to refresh, documents reading, needs insurance, second payer, linked, in-note, not-saved, edited, working / idle — and what each Home area is for and where its items are resolved.",
      },
      {
        h: "Leading well",
        p: "You know which actions can't be undone (deleting a visit, deleting remittance rows, the permanent history, discarding unsaved changes, unsigning after a claim) and you guide teammates before they act. When something's unfamiliar, you reason from how the product fits together to a safe next step.",
      },
    ],
    groups: [
      {
        id: "l4-a",
        title: "Whole-product fluency",
        items: [
          "I can give a one-line summary of what AriaOne does and show any part of it quickly.",
          "I can walk the full journey without notes (setup → book → visit → note → sign → claim → payment → 835 → history).",
          "I can show the same patient's data appearing consistently across the chart, documents and insurance.",
          "I can explain every number, badge and status and what causes it.",
          "I can explain each Home area and where its items are resolved.",
        ],
      },
      {
        id: "l4-b",
        title: "Train your team",
        items: [
          "I can run a first-day session for a new user.",
          "I can coach a teammate through the daily workflow and the harder cases.",
          "I can create a practical task for any feature so people learn by doing.",
          "I keep our team's quick answers up to date when the product changes.",
          "I run a regular 'tricky cases' review with the team.",
        ],
      },
      {
        id: "l4-c",
        title: "Run a smooth setup",
        items: [
          "I can set up a new practice end to end: providers, availability, note template, visit types and coding rules, plus a first-week plan.",
          "I can lead a billing session on insurance, group fixes and two-insurance cases.",
          "I can lead a clinical session on the note editor, pull-from-chart and signing.",
          "I can lead a scheduling session on availability, booking and running visits.",
        ],
      },
      {
        id: "l4-d",
        title: "Good judgement",
        items: [
          "When something's unfamiliar, I reason from how the product fits together to a safe next step.",
          "I know the actions that can't be undone and coach people before they act.",
          "I know what the product can't undo and when to ask for help.",
        ],
      },
      {
        id: "l4-kc",
        title: "Champion self-check — read & confirm",
        qa: [
          { q: "What one misunderstanding causes the most early confusion for new users?", a: "Not checking which practice is active — confirming the right practice resolves most 'I can't find it' moments." },
          { q: "A teammate asks why their claim won't save. What's your quick checklist?", a: "Is the visit signed? Are there items still to fix (like a diagnosis pointer)? Is the payer and billing NPI set?" },
          { q: "Why can the 'needs insurance' count differ between Home, Encounter review and Claims queue?", a: "They're scoped differently — Encounter review counts the whole practice, while filters and other pages can show a subset." },
          { q: "A new user wants to delete a visit 'to start over'. What do you tell them?", a: "It can't be undone — suggest rebooking or ending the session instead, depending on the case." },
          { q: "Someone says a page 'looks different' and thinks it's broken. Your take?", a: "It's just styling — the actions and data are the same." },
        ],
      },
      {
        id: "l4-pt",
        title: "Mastery tasks — demonstrate, then tick",
        items: [
          "Give a full-journey walkthrough to a teammate without notes.",
          "Teach the note editor to a new user in ten minutes.",
          "Lead a new-practice setup from start to finish.",
          "Run a short 'tricky cases' session with your team.",
          "Use the activity dashboard to explain where a batch stands.",
        ],
      },
    ],
  },
];

// Flattened list of checklist item ids for a level (items + qa).
export function levelItemIds(level) {
  const ids = [];
  for (const g of level.groups) {
    const arr = g.items || g.qa || [];
    arr.forEach((_, i) => ids.push(`${g.id}-${i}`));
  }
  return ids;
}

export function levelItemCount(level) {
  return levelItemIds(level).length;
}
